<script setup lang="ts">
import { v4 as uuid } from 'uuid'
import type { CreatorCategory } from '@store'
import type { DaoCreatorsFilter } from '@store/creator/type'
import type {
  SorterByButtonData,
  SorterByButtonDataMutable,
} from '@components/NovaBoxSorter/NovaBoxSorter.types'
import { ROUTES } from '@configs'

type ActivityDaoCreatorFilterItem = SorterByButtonDataMutable<
  SorterByButtonData<DaoCreatorsFilter>
>
definePageMeta({
  layout: 'one-column',
})

useSetSEO()
const { t, messages, locale } = useI18n()
const creatorStore = useCreatorStore()
const { gtEvent } = useGoogleTag()
const fetchCount = ref(uuid())
const route = useRoute()

const creatorCategoriesStatus = reactive({
  isLoading: false,
  isError: false,
})
const allCreatorCategories = computed<CreatorCategory[]>(() =>
  [
    {
      crtrCount: 0,
      crtrCtgrySn: -1,
      ctgryDc: '',
      ctgryNm: 'All',
      mainCrtrCount: 0,
    },
  ].concat(creatorStore.creatorCategories)
)
const selectCreatorCategorySn = ref<CreatorCategory['crtrCtgrySn']>(-1)
const creatorsStatus = reactive({
  isLoading: false,
  isError: false,
})
const creatorsListRef = ref<Element | null>(null)

// 뒤로가기
const handleOnGoBack = () => {
  gtEvent('clickEvent', {
    eventCategory: '클릭',
    eventAction: '홈으로 이동',
    eventLabel: '뒤로가기',
    eventSlot: '',
    eventI18nAddr: '',
    eventComponent: 'NovaButtonIcon',
  })
  useNavigations({ type: 'back' })
}
// 크리에이터 DAO 필터 리스트
const filterItems = computed(
  () =>
    Object.keys((messages.value[locale.value].dao as any).filter.creator)
      .filter((key) => key !== 'dao')
      .map((key) => {
        const _key = key as DaoCreatorsFilter
        return {
          id: _key,
          name: `${t(`dao.filter.creator.${_key}`)}`,
        }
      }) as ActivityDaoCreatorFilterItem[]
)
// 크리에이터 DAO 필터 변경
const activeFilter = ref<ActivityDaoCreatorFilterItem>(filterItems.value[0])
// 크리에이터 DAO 필터 변경
const handleOnUpdateSorter = async (newSortItem) => {
  activeFilter.value = newSortItem
  await fetchCreators(true)
}

// 크리에이터 카테고리 변경
const handleOnUpdateCreatorCategory = async (
  crtrCtgrySn: CreatorCategory['crtrCtgrySn']
) => {
  selectCreatorCategorySn.value = crtrCtgrySn
  await fetchCreators(true)
}

// 크리에이터 카테고리 목록 조회
const fetchCreatorCategories = async () => {
  try {
    creatorCategoriesStatus.isLoading = true
    creatorCategoriesStatus.isError = false
    await creatorStore.fetchCreatorCategories({ crtrOrgnztTyCode: 'ALL' })
  } catch {
    creatorCategoriesStatus.isError = true
  } finally {
    creatorCategoriesStatus.isLoading = false
  }
}

// 크리에이터 목록 조회
const fetchCreators = async (refresh = false) => {
  try {
    creatorsStatus.isLoading = true
    creatorsStatus.isError = false
    await creatorStore.fetchCreators({
      payload: {
        pageSize: 20,
        crtrCtgrySn: selectCreatorCategorySn.value,
        creatorDaoOpenedAt: activeFilter.value.id === 'dao' ? 'Y' : 'N',
      },
      refresh,
    })

    const after = uuid()

    fetchCount.value = after
  } catch {
    creatorsStatus.isError = true
  } finally {
    creatorsStatus.isLoading = false
  }
}

onBeforeMount(async () => {
  await fetchCreatorCategories()
  if (route.query.tab === 'dao') {
    await handleOnUpdateSorter({ id: 'dao', name: 'DAO' })
    await useNavigations({ url: ROUTES.CREATORS_ALL.path, type: 'replace' })
    // replace
  } else {
    await fetchCreators(true)
  }
})
</script>

<template>
  <div class="page-creators">
    <div class="page-title-bar">
      <NovaButtonIcon
        :icon="{ type: 'outline', name: 'chev-left' }"
        class="btn-go-back"
        @click="handleOnGoBack"
      />

      <h4 class="title">{{ t('home.creator') }}</h4>
    </div>
    <!-- DAO 필터 -->
    <div class="creator-filter-box">
      <NovaBoxSorter
        :active-sort-item="activeFilter"
        :sort-items="filterItems"
        @on-change-active-sort-item="handleOnUpdateSorter"
      />
    </div>
    <NovaBoxCreatorCategories
      :creator-categories="allCreatorCategories"
      :select-creator-category-sn="selectCreatorCategorySn"
      :is-loading="creatorCategoriesStatus.isLoading"
      :is-error="creatorCategoriesStatus.isError"
      :ga-slot="t('home.creator')"
      :with-bar="true"
      @update:category-change="handleOnUpdateCreatorCategory"
    />

    <div ref="creatorsListRef" class="page-contents">
      <NovaBoxEmptyMessage
        v-if="
          creatorStore.creators.refresh &&
          !creatorStore.creators.items.creatorList.length &&
          !creatorsStatus.isError
        "
        :message="$t('statusMessages.creators.empty')"
        :fill="true"
      />

      <NovaBoxEmptyMessage
        v-if="
          !creatorStore.creators.items.creatorList.length &&
          creatorsStatus.isError
        "
        :message="$t('statusMessages.creators.error')"
        :fill="true"
      />
      <NovaRecyclerGrid
        v-if="creatorStore.creators.items.creatorList.length"
        :key="fetchCount"
        :items="creatorStore.creators.items.creatorList"
        :is-last="creatorStore.creators.last"
        :is-loading="creatorsStatus.isLoading"
        :is-error="creatorsStatus.isError"
        :is-refresh="creatorStore.creators.refresh"
        :key-name="'userSn'"
        :page-mode="true"
        :error-message="t('statusMessages.creators.error')"
        :responsive="{
          mobile: {
            columns: 3,
            rowGap: 14,
            columnGap: 14,
          },
          tablet: {
            columns: 4,
            rowGap: 20,
            columnGap: 20,
          },
          desktop: {
            columns: 5,
            rowGap: 20,
            columnGap: 20,
          },
        }"
        @load-more="() => fetchCreators(false)"
      >
        <template #item="{ item }">
          <div class="recycler-view-grid-item">
            <NovaCreatorProfileCard :creator="item" />
          </div>
        </template>
      </NovaRecyclerGrid>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.page-creators {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-top: 30px;
  overflow-x: clip;
  @include mobile {
    gap: 14px;
    padding-left: 10px;
    padding-right: 10px;
  }
  .page-title-bar {
    display: flex;
    align-items: center;
    gap: 10px;
    padding-left: 10px;
    padding-right: 10px;

    .btn-go-back {
      color: $color-text-3;
    }

    .title {
      @include text-style($text-display-bold);
      color: $color-black;
    }
  }
}

@include mobile {
  .page-creators {
    gap: 14px;
    padding-top: 20px;
  }
}
</style>
